import isEqual from 'deep-equal';
import type { NextRouter } from 'next/router';
import { withRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import type { DeviceType, FhhState, Filters, UnleashToggles } from '@/domain';
import * as resultActions from '@/ducks/results/resultsActions';
import { trackResultPage } from '@/lib/tracking/tracking';
import { FhhFullScreenMap } from '../../components/fullScreenMap/FullScreenMap';
import { ResultFilterMap } from '../../components/resultFilterMap/ResultFilterMap';
import { ResultListMap } from '../../components/resultListMap/ResultListMap';
import { TopBarMap } from '../topBarMap/TopBarMap';
import { TopBarMapMobile } from '../topBarMap/TopBarMapMobile';

import 'shared/map/map.css';

interface ResultMapState {
    isMobile: boolean;
    filtersOpen: boolean;
    selectedAdId: string | null;
    windowSize: number[];
}

interface ResultMapProps {
    deviceType: DeviceType;
    loginId?: number;
    unleash?: UnleashToggles;
    router: NextRouter;
}

export const ResultMap = ({ deviceType, loginId, router }: ResultMapProps) => {
    const { apiExternalUrl } = useConfig();
    const dispatch = useDispatch();
    const { data, filters, selectedAd, filterData } = useSelector((state: FhhState) => state.results);
    const [state, setState] = useState<ResultMapState>({
        isMobile: !!deviceType.isProbablyMobile,
        filtersOpen: false,
        selectedAdId: null,
        windowSize: [],
    });
    const [isClient, setIsClient] = useState(false);

    const prevFiltersRef = useRef<Filters>(filters);
    const prevDataStatsRef = useRef(data.stats);
    const hasInitiallyPopulatedMapResults = useRef(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (!hasInitiallyPopulatedMapResults.current && isClient) {
            const query = router?.query || {};
            const mapFilters = Object.assign({}, filters, query);

            if (data.listMap === null) {
                dispatch(resultActions.populateMapResults(apiExternalUrl, mapFilters));
            }
            if (data.resultItems === null) {
                dispatch(resultActions.populateResults(apiExternalUrl, mapFilters));
            }

            hasInitiallyPopulatedMapResults.current = true;
        }
    }, [apiExternalUrl, filters, router?.query, data.listMap, data.resultItems, dispatch, isClient]);

    const toggleFilterState = useCallback(() => {
        setState((prev) => ({
            ...prev,
            filtersOpen: !prev.filtersOpen,
        }));
    }, []);

    const handleResize = useCallback(() => {
        if (typeof window !== 'undefined') {
            setState((prev) => ({
                ...prev,
                windowSize: [window.innerWidth, window.innerHeight],
            }));
        }
    }, []);

    const populateResultsIfEmpty = useCallback(() => {
        const query = router?.query || {};

        if (data.resultItems === null) {
            dispatch(resultActions.populateResults(apiExternalUrl, Object.assign({}, filters, query)));
        }
    }, [router?.query, data.resultItems, filters, dispatch]);

    const isFalseyOrEmpty = useCallback((value: unknown): boolean => {
        return !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}');
    }, []);

    useEffect(() => {
        if (state.windowSize.length > 0) {
            let rowCoefficient = 1;
            if (!state.isMobile) {
                rowCoefficient = 6;
            }
            const rows = Math.floor(state.windowSize[1] / 200) + rowCoefficient;
            const columns = Math.floor(state.windowSize[0] / 200) + 4;
            dispatch(resultActions.updateRowsAndColumnsForMap(rows, columns));
        }
        populateResultsIfEmpty();
        dispatch(resultActions.setNumberOfFusAdsMapDefault(filters));
        if (!isFalseyOrEmpty(router?.query)) {
            dispatch(resultActions.updateFilters(router.query as Partial<Filters>));
        }
        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const filtersChanged = !isEqual(filters, prevFiltersRef.current);
        const statsChanged = !isEqual(data.stats, prevDataStatsRef.current);

        if (filtersChanged) {
            router?.replace({ pathname: router.pathname, query: filters }, undefined, { shallow: true });
        }

        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (statsChanged || filtersChanged) {
            if (data.stats?.numResults !== undefined) {
                // Pulse tracker id: FHH_pv08 (see tracking section in README.md)
                trackResultPage('Map', { data, filters });
            }
        }

        prevFiltersRef.current = filters;
        prevDataStatsRef.current = data.stats;
    }, [filters, data.stats, router]);

    const boundFilterResults = useCallback(
        (filter: Filters) => {
            return dispatch(resultActions.filterResults(apiExternalUrl, filter));
        },
        [dispatch],
    );

    return (
        <>
            <div className="map-result-list px-8 hidden md:block bg-white">
                <TopBarMap filtersOpen={state.filtersOpen} toggleFilterState={toggleFilterState} />
                <ResultListMap
                    results={data.resultItems || false}
                    stats={data.stats as Record<string, unknown>}
                    filters={filters}
                    onFilterChanged={boundFilterResults}
                    numberOfPreloadedImages={6}
                    loginId={loginId}
                />
            </div>
            <TopBarMapMobile filtersOpen={state.filtersOpen} toggleFilterState={toggleFilterState} />
            <FhhFullScreenMap
                filters={filters}
                data={data}
                populateMapResults={(filters) => dispatch(resultActions.populateMapResults(apiExternalUrl, filters))}
                onFilterChanged={boundFilterResults}
                selectedAdId={state.selectedAdId ?? selectedAd?.id?.toString()}
                populateMapObject={(adId) => dispatch(resultActions.populateMapObject(adId))}
                selectedAdData={
                    selectedAd || {
                        id: '',
                        heading: '',
                        ad_type: '',
                        sleeping_capacity: 0,
                        no_of_bedrooms: 0,
                        property_type: 0,
                        supplier_name: '',
                        latitude: 0,
                        longitude: 0,
                        imageurl: '',
                        bookings_enabled: false,
                    }
                }
            />
            <ResultFilterMap filtersOpen={state.filtersOpen} filters={filters} filterResults={boundFilterResults} filterData={filterData} />
        </>
    );
};

export default withRouter(ResultMap);
